import { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { ModalForm, Notice, Select } from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { ClockAdjustmentFields, validateClockAdjustment } from "@components/ClockAdjustments";
import { isoToDate } from "@helpers/localDate";
import { clockDateTime } from "@helpers/timeClock";
import { focusFirstError } from "../../Common/FormPage";
import { labelWithHint, searchLabelAndHint } from "@components/OptionWithHint";
import { employeeOptionsOf } from "./employeeOptions";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const IDS = { date: "direct-adjustment-date", time: "direct-adjustment-time", reason: "direct-adjustment-reason" };
const FIELDS = ["direct-adjustment-employee", IDS.date, IDS.time, IDS.reason];

function validate(values) {
  const errors = {};
  if (!values.contractId) errors["direct-adjustment-employee"] = "Selecione o colaborador";
  return { ...errors, ...validateClockAdjustment(values, IDS) };
}

/**
 * Ajuste direto: o ADM ou o gestor de ponto (time_clock:create_adjustment) inclui a
 * batida no espelho do colaborador, já aprovada. O gestor não ajusta o próprio
 * contrato (o back também barra).
 *
 * Props: onClose, onSaved.
 */
export default function DirectAdjustmentModal({ onClose, onSaved }) {
  const timeClockService = useService("timeClock");
  const auth = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const isAdmin = auth.role === "ADM";

  const [contracts, setContracts] = useState(null);
  const [values, setValues] = useState({ contractId: "", date: null, time: "", reason: "" });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const result = await timeClockService.listCltContracts();
      // Em erro, o sendError do service já mostrou o toast
      const data = result && !result.error ? result.data?.list || result.data : null;
      let list = Array.isArray(data) ? data : [];
      if (!isAdmin) list = list.filter((contract) => contract.user_id !== auth.id);
      setContracts(list);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errors = submitted ? validate(values) : {};
  const set = (field, value) => setValues((prev) => ({ ...prev, [field]: value }));

  const options = employeeOptionsOf(contracts || []);
  const contract = (contracts || []).find((item) => String(item.id) === values.contractId);
  // A batida não pode ser anterior ao início do contrato (o back confere no dia local)
  const minDate = contract?.start_date ? isoToDate(contract.start_date) : undefined;

  function close() {
    if (!saving) onClose();
  }

  async function save() {
    setSubmitted(true);
    const found = validate(values);
    if (Object.keys(found).length) {
      focusFirstError(FIELDS.filter((id) => found[id]));
      return;
    }

    setSaving(true);
    const result = await timeClockService.createDirectAdjustment({
      contract_id: parseInt(values.contractId),
      requested_clock_time: clockDateTime(values.date, values.time).toISOString(),
      reason: values.reason.trim(),
    });
    setSaving(false);
    // Em erro, o sendError do service já mostrou o toast
    if (!result || result.error) return;
    toast.success("Ajuste registrado! A batida foi incluída no espelho.");
    onSaved();
  }

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={saving}
      $isLoading={saving}
      $size="medium"
      $title="Criar ajuste de ponto"
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: saving },
        { text: "Criar ajuste", action: save, variant: "primary", isDisabled: saving },
      ]}>
      <Stack>
        <Notice
          $variant="gray"
          $text="A batida entra no espelho do colaborador já aprovada, com o registro de quem fez o ajuste. As batidas originais não mudam."
        />
        <Select
          $idForm="direct-adjustment-employee"
          $label="Colaborador"
          $isRequired
          $errors={errors["direct-adjustment-employee"]}
          $options={options}
          $textNoOptions="Nenhum colaborador encontrado"
          isLoading={contracts === null}
          placeholder="Selecione o colaborador"
          formatOptionLabel={labelWithHint}
          filterOption={searchLabelAndHint}
          value={options.find((option) => option.value === values.contractId) || null}
          onChange={(option) => option && set("contractId", option.value)}
        />
        <ClockAdjustmentFields
          ids={IDS}
          values={values}
          errors={errors}
          onChange={set}
          minDate={minDate}
          reasonPlaceholder="Ex.: o colaborador esqueceu de registrar a saída para o almoço"
        />
      </Stack>
    </ModalForm>
  );
}
